import type { ButtonHTMLAttributes, Ref } from 'react';
import { DISABLED, FOCUS_RING, MOTION_COLORS, TOUCH_TARGET } from '../internal/classes';
import { cx } from '../internal/cx';
import type { ButtonSize, ButtonVariant } from './Button';
import { Icon, type IconName } from './Icon';
import { Spinner } from './Spinner';

const BASE = cx(
  'inline-flex aspect-square items-center justify-center rounded-sm select-none cursor-pointer',
  FOCUS_RING,
  MOTION_COLORS,
  TOUCH_TARGET,
  DISABLED,
);

const VARIANT_CLASSES: Readonly<Record<ButtonVariant, string>> = {
  primary: 'bg-brand text-text-on-brand hover:bg-(--ui-brand-hover)',
  secondary: 'border border-border-strong bg-surface text-text hover:bg-surface-sunken',
  ghost: 'text-text-secondary hover:bg-surface-sunken hover:text-text',
  danger: 'bg-(--ui-danger-strong) text-text-on-brand hover:bg-(--ui-danger-strong-hover)',
};

const SIZE_CLASSES: Readonly<Record<ButtonSize, string>> = {
  sm: 'size-(--ui-control-sm)',
  md: 'size-(--ui-control-md)',
  lg: 'size-(--ui-control-lg)',
};

/** Props of {@link IconButton}. */
export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /**
   * What the button does, in the reader's language. Required: it is the button's only accessible
   * name, and it is what a `<Tooltip>` around the button should show.
   */
  label: string;
  /** Which glyph to draw. */
  icon: IconName;
  /** How much weight the action carries; `ghost` by default. */
  variant?: ButtonVariant;
  /** Control height; `md` by default. */
  size?: ButtonSize;
  /** Shows a spinner instead of the glyph and blocks further clicks. */
  loading?: boolean;
  /** Ref to the rendered element. */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * A square button that shows only an icon. The `label` is mandatory, so such a button can never end
 * up without an accessible name (WCAG 2.2 AA, 4.1.2).
 *
 * @example <IconButton icon="archive" label={t('mail.archive')} onClick={archive} />
 */
export function IconButton({
  label,
  icon,
  variant = 'ghost',
  size = 'md',
  loading = false,
  className,
  disabled = false,
  type,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type ?? 'button'}
      aria-label={label}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cx(BASE, VARIANT_CLASSES[variant], SIZE_CLASSES[size], className)}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : <Icon name={icon} size={size} />}
    </button>
  );
}
